import React from "react";
import Logo from "./Logo";

/**
 * LoadingScreen — branded full-screen overlay shown during auth flows.
 *
 * Uses the existing Dnya logo (via <Logo/>), a breathing gradient circle,
 * a spinning ring, "Assalamu Alaikum" title, a soft subtitle, and three
 * pulsing dots. Colours are strictly from the approved palette:
 *   background radial: #0f3d0f centre → #0a2e0a edges
 *   circle gradient:   #4a9a4a → #1a5c1a
 *   spinner ring:      #CEF0CE
 *   title text:        #CEF0CE
 *   subtitle:          #7dc47d (approved muted green — sits between mosaic2 and high1)
 */
export default function LoadingScreen({
  title = "Assalamu Alaikum",
  subtitle = "Preparing your space, Insha'Allah",
}) {
  return (
    <div
      data-testid="loading-screen"
      role="status"
      aria-live="polite"
      style={{
        position: "fixed", inset: 0, zIndex: 6000,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        background:
          "radial-gradient(circle at center, #0f3d0f 0%, #0a2e0a 80%)",
        color: "#CEF0CE",
        gap: 22,
      }}
    >
      <div style={{ position: "relative", width: 132, height: 132 }}>
        {/* Spinner ring around the logo circle */}
        <div
          style={{
            position: "absolute", inset: 0,
            borderRadius: "50%",
            border: "2px solid transparent",
            borderTopColor: "#CEF0CE",
            animation: "dnya-ls-spin 1.2s linear infinite",
          }}
        />
        {/* Breathing gradient disc holding the Dnya logo */}
        <div
          style={{
            position: "absolute", inset: 12,
            borderRadius: "50%",
            background: "linear-gradient(145deg, #4a9a4a, #1a5c1a)",
            boxShadow: "0 0 40px rgba(74, 154, 74, 0.35)",
            display: "flex", alignItems: "center", justifyContent: "center",
            animation: "dnya-ls-breathe 2.5s ease-in-out infinite",
          }}
        >
          <Logo size={64} />
        </div>
      </div>

      <div
        className="font-dnya"
        style={{
          fontFamily: "Cinzel, serif",
          fontSize: 15,
          color: "#CEF0CE",
          letterSpacing: "0.05em",
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: 12,
          color: "#7dc47d",
          maxWidth: 320,
          textAlign: "center",
          fontStyle: "italic",
        }}
      >
        {subtitle}
      </div>
      <div style={{ display: "flex", gap: 6 }} aria-hidden="true">
        <span style={dot("0s")} />
        <span style={dot("0.2s")} />
        <span style={dot("0.4s")} />
      </div>

      <style>{`
        @keyframes dnya-ls-spin {
          from { transform: rotate(0deg); } to { transform: rotate(360deg); }
        }
        @keyframes dnya-ls-breathe {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.04); }
        }
        @keyframes dnya-ls-dot {
          0%, 80%, 100% { opacity: 0.25; transform: scale(0.85); }
          40%           { opacity: 1;    transform: scale(1.15); }
        }
      `}</style>
    </div>
  );
}

const dot = (delay) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  background: "#CEF0CE",
  display: "inline-block",
  animation: `dnya-ls-dot 1.4s ease-in-out ${delay} infinite`,
});
